import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";

import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";

import { BLOG } from "@/data/blog";
import { KENNISBANK } from "@/data/kennisbank";

/**
 * Built-in site search at `/zoeken`. Powers the WebSite SearchAction JSON-LD
 * target so Google can render the sitelinks search box for `equisight.nl`.
 *
 * Pure client-side fuzzy match across blog + knowledge base — keeps the page
 * crawlable without paying for an external search service.
 */

const normalise = (s) =>
  (s ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const buildIndex = () => {
  const blogEntries = Object.values(BLOG || {}).map((b) => ({
    type: "blog",
    typeLabel: "Blog",
    slug: b.slug,
    title: b.title,
    lead: b.lead,
    category: b.category,
    href: `/blog/${b.slug}`,
  }));
  const kbEntries = Object.values(KENNISBANK || {}).map((k) => ({
    type: "kennisbank",
    typeLabel: "Kennisbank",
    slug: k.slug,
    title: k.title,
    lead: k.lead,
    category: k.category,
    href: `/kennisbank/${k.slug}`,
  }));
  return [...blogEntries, ...kbEntries].map((e) => ({
    ...e,
    haystack: normalise(`${e.title} ${e.lead} ${e.category}`),
  }));
};

const scoreMatch = (entry, terms) => {
  if (!terms.length) return 0;
  const titleN = normalise(entry.title);
  let score = 0;
  for (const t of terms) {
    if (!t) continue;
    if (titleN.includes(t)) score += 6;
    if (entry.haystack.includes(t)) score += 1;
  }
  return score;
};

export const ZoekenPage = () => {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);

  // Keep input in sync with URL when navigating back/forward
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);

  const index = useMemo(buildIndex, []);

  const results = useMemo(() => {
    const terms = normalise(q).split(/\s+/).filter((t) => t.length >= 2);
    if (!terms.length) return [];
    return index
      .map((e) => ({ ...e, score: scoreMatch(e, terms) }))
      .filter((e) => e.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 50);
  }, [q, index]);

  const onSubmit = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    setParams(next, { replace: true });
  };

  return (
    <main data-testid="page-zoeken" className="bg-background">
      <Seo
        title={q ? `Zoeken: ${q} | EquiSight` : "Zoek in EquiSight — paardenkennis & gidsen"}
        description="Doorzoek alle EquiSight-blogs, gidsen en kennisartikelen over paardenverzorging, training, gezondheid en management."
        path="/zoeken"
        keywords={["paarden zoeken", "EquiSight zoek", "paardenkennis"]}
        breadcrumbs={[{ name: "Zoeken", path: "/zoeken" }]}
        noindex={!!q}
      />
      <AnnouncementBar />
      <Header />
      <Breadcrumbs items={[{ name: "Zoeken", path: "/zoeken" }]} />

      <section className="mx-auto max-w-3xl px-6 py-16 lg:px-10 lg:py-24">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
          Zoeken
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold sm:text-5xl">
          Vind een onderwerp in EquiSight.
        </h1>
        <p className="mt-4 text-base text-[hsl(var(--ink-soft))]">
          Doorzoek 160+ blogs, 8 kennisartikelen en alle cornerstone-gidsen.
        </p>

        <form onSubmit={onSubmit} role="search" className="mt-8">
          <label className="flex items-center gap-3 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-5 py-3 focus-within:border-[hsl(var(--forest))]">
            <SearchIcon className="h-4 w-4 text-[hsl(var(--ink-soft))]" />
            <input
              data-testid="search-input"
              type="search"
              name="q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Probeer: koliek, hoefverzorging, fokkerij…"
              className="flex-1 bg-transparent text-base text-[hsl(var(--ink))] outline-none placeholder:text-[hsl(var(--ink-soft))]"
              autoFocus
              autoComplete="off"
            />
            <button
              type="submit"
              data-testid="search-submit"
              className="rounded-full bg-[hsl(var(--forest))] px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Zoek
            </button>
          </label>
        </form>

        <div className="mt-10" data-testid="search-results">
          {q && results.length === 0 && (
            <p className="text-sm text-[hsl(var(--ink-soft))]">
              Geen resultaten voor <strong>"{q}"</strong>. Probeer een ander
              zoekwoord of bekijk de <Link to="/blog" className="underline">volledige blog</Link>.
            </p>
          )}
          {results.length > 0 && (
            <>
              <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-soft))]">
                {results.length} {results.length === 1 ? "resultaat" : "resultaten"}
              </p>
              <ul className="mt-4 space-y-3">
                {results.map((r) => (
                  <li key={`${r.type}-${r.slug}`}>
                    <Link
                      to={r.href}
                      data-testid={`search-result-${r.type}-${r.slug}`}
                      className="group block rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 transition-colors hover:border-[hsl(var(--forest))]"
                    >
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--sand-dark))]">
                        {r.typeLabel} · {r.category}
                      </p>
                      <h2 className="mt-1 font-display text-lg font-semibold text-[hsl(var(--ink))] group-hover:text-[hsl(var(--forest))]">
                        {r.title}
                      </h2>
                      <p className="mt-1 line-clamp-2 text-sm text-[hsl(var(--ink-soft))]">
                        {r.lead}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
          {!q && (
            <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-6 text-center text-sm text-[hsl(var(--ink-soft))]">
              Begin met typen om resultaten te zien.
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ZoekenPage;
